import React, { useEffect, useRef, useState } from 'react';
import '../../styles/TitTatCarousel.css';
import Button from '../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRupeeSign, faGavel } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import titService from '../../services/titService';
import auctionService from '../../services/auctionService';

const generateTokenSymbol = (length = 7) => {
  if (length < 4) throw new Error("Length must be at least 4");

  const english = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // exclude confusing letters like I, O
  const specials = '!@#$%&?'; // allowed special characters

  let result = '';

  for (let i = 0; i < length; i++) {
    if (i === 3) {
      // 4th character is special
      result += specials.charAt(Math.floor(Math.random() * specials.length));
    } else {
      const chars = english;
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
  }

  return result;
};

const AuctionSessionModal = ({ tokenId, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    token_symbol: generateTokenSymbol(4),
    starting_price: '',
    min_bid_increment: 1,
    max_participants: 20,
    duration_minutes: 10,
    anti_sniping_seconds: 0,
    metadata: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    onSubmit({
      token_symbol: form.token_symbol,
      starting_price: Number(form.starting_price),
      min_bid_increment: Number(form.min_bid_increment),
      max_participants: Number(form.max_participants),
      duration_minutes: Number(form.duration_minutes),
      anti_sniping_seconds: Number(form.anti_sniping_seconds)
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create Auction Session</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="form-warning">
          Please review auction parameters carefully before starting.
        </div>

        <div className="form-group">
          <label>Token Symbol</label>
          <input
            name="token_symbol"
            value={form.token_symbol}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Starting Price</label>
          <input type="number" name="starting_price" onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Min Bid Increment</label>
          <input type="number" name="min_bid_increment" onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Duration (minutes)</label>
          <input type="number" name="duration_minutes" onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Max Participants</label>
          <input type="number" name="max_participants" onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Anti-sniping Seconds</label>
          <input type="number" name="anti_sniping_seconds" onChange={handleChange} />
        </div>


        <div className="form-actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="add" onClick={handleSubmit}>Create & Start</Button>
        </div>
      </div>
    </div>
  );
};


export default function TokenCarousel({ data }) {
  const containerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [activeTokenId, setActiveTokenId] = useState(null);
  const { user } = useAuth();

  const isManager = user?.role === 'manager' || user?.is_manager === true;
  const isArtist = user?.role === 'artist' || user?.is_artist === true;
  const canOpenBid = isManager;
  const canPushToBid = isArtist;

  const handlePushToBid = async (tokenId) => {
    try {
      await titService.pushTokenToBid(tokenId);
      // Refresh data or show success message
    } catch (error) {
      console.error('Error pushing token to bid:', error);
    }
  };

  const handleCreateSession = async (payload) => {
    try {
      const res = await auctionService.createAuctionSession(activeTokenId, payload);
      const sessionId = res.data.session.session_id;

      await auctionService.startAuctionSession(sessionId);

      setShowModal(false);
      setActiveTokenId(null);
    } catch (err) {
      console.error(err.response?.data || err);
    }
  };
  // Auto-scroll
  useEffect(() => {
    if (!containerRef.current || isHovered) return;

    const interval = setInterval(() => {
      containerRef.current.scrollLeft += 320;

      if (
        containerRef.current.scrollLeft + containerRef.current.offsetWidth >=
        containerRef.current.scrollWidth
      ) {
        containerRef.current.scrollLeft = 0;
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovered]);

  return (
    <>
      <div
        className="carousel-wrapper"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div ref={containerRef} className="carousel-container">
          {data.map((item) => {
            const isBidOn = item.bid_pushed;

            return (
              <div
                key={item.token_id}
                className={`carousel-card ${isBidOn ? 'bid-on' : 'bid-off'}`}
              >
                <p>{item.art_id}</p>

                <div className="price card-item">
                  <span>Total:</span> <FontAwesomeIcon icon={faRupeeSign} />
                  {item.token_details.total_price}
                </div>

                <div className="sub-token-count card-item">
                  Units: {item.token_details.sub_tokens.length}
                </div>

                <div className="sub-token-count card-item">
                  Sub Tats Price : <FontAwesomeIcon icon={faRupeeSign} />
                  {item.token_details.price_per_token}
                </div>

                <div className="sub-token-count card-item">
                  Available Sub Tats: {
                    item.token_details.sub_tokens.filter(
                      sub => sub.status === "available"
                    ).length
                  }
                </div>

                {/* 🔥 STATUS SECTION */}
                <div className="status card-item">
                  {/* READ-ONLY USERS */}
                  {!canOpenBid && canPushToBid && (
                    <span>
                      Pushed to Bid: {isBidOn ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
                <div className="card-item">
                  {/* MANAGER LOGIC */}
                  {canOpenBid && isBidOn && (
                    <Button
                      variant="add"
                      onClick={() => {
                        setActiveTokenId(item.token_id);
                        setShowModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faGavel} /> Open Bid Session
                    </Button>

                  )}

                  {/* ARTIST LOGIC */}
                  {canPushToBid && !isBidOn && (
                    <Button
                      variant="add"
                      onClick={() => handlePushToBid(item.token_id)}
                    >
                      <FontAwesomeIcon icon={faGavel} />  Push to Bid
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* ✅ MODAL RENDER */}
      {showModal && (
        <AuctionSessionModal
          tokenId={activeTokenId}
          onClose={() => {
            setShowModal(false);
            setActiveTokenId(null);
          }}
          onSubmit={handleCreateSession}
        />
      )}
    </>
  );
}
